import { useQuery } from "@tanstack/react-query"
import { getSingleJourney } from "@/lib/api/quests"
import { JourneyStage, Journey } from "@/types/quests"
import { useMemo, useCallback } from "react"
import { queryKeys } from "@/lib/utils/query"

type StageProgress = JourneyStage & {
  completedMissions: number
  totalMissions: number
  progressPercentage: number
  isCompleted: boolean
}

export type NextJourneyMissionInfo = {
  journeyMissionId: string
  missionId: string
  missionNumber: number
  missionTitle: string
  isNextStage?: boolean
  nextStageTitle?: string
}

type JourneyData = {
  journey: Journey
  stages: JourneyStage[]
  currentStageIndex: number
  currentStage: JourneyStage | null
  stagesProgress: StageProgress[]
  questProgressPercentage: number
  totalMissions: number
  completedMissions: number
  completedStageIds: Set<string>
  nextJourneyMission: NextJourneyMissionInfo | null
}

export const useJourneyData = (journeyId: string) => {
  const { data, isLoading, error } = useQuery<Journey>({
    queryKey: queryKeys.journey(journeyId),
    queryFn: () => getSingleJourney(journeyId),
    enabled: !!journeyId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  })

  const isStageCompleted = (stage: JourneyStage): boolean =>
    stage.state === "OK"

  const processedData = useMemo<JourneyData | null>(() => {
    if (!data) return null

    const stages = data.journey_stages || []

    const currentStageIndex = stages.findIndex(
      (stage) => !isStageCompleted(stage),
    )
    const activeStageIndex =
      currentStageIndex === -1
        ? Math.max(0, stages.length - 1)
        : currentStageIndex
    const currentStage = stages[activeStageIndex] || null

    const stagesProgress = stages.map((stage) => {
      const { journey_missions } = stage
      const totalMissions = journey_missions.length
      const completedMissions = journey_missions.filter(
        (m) => m.state === "OK",
      ).length

      return {
        ...stage,
        completedMissions,
        totalMissions,
        progressPercentage:
          totalMissions > 0
            ? Math.round((completedMissions / totalMissions) * 100)
            : 0,
        isCompleted: isStageCompleted(stage),
      }
    })

    const { totalMissions, completedMissions } = stages.reduce(
      (acc, stage) => ({
        totalMissions: acc.totalMissions + stage.journey_missions.length,
        completedMissions:
          acc.completedMissions +
          stage.journey_missions.filter((m) => m.state === "OK").length,
      }),
      { totalMissions: 0, completedMissions: 0 },
    )

    const questProgressPercentage =
      totalMissions > 0
        ? Math.round((completedMissions / totalMissions) * 100)
        : 0

    const completedStageIds = new Set(
      stages.filter(isStageCompleted).map((stage) => stage.id),
    )

    const getNextMission = (): NextJourneyMissionInfo | null => {
      if (!currentStage) return null

      const pendingJourneyMission = currentStage.journey_missions.find(
        (journeyMission) => journeyMission.state !== "OK",
      )

      if (pendingJourneyMission) {
        return {
          journeyMissionId: pendingJourneyMission.id,
          missionId: pendingJourneyMission.mission.id,
          missionNumber:
            currentStage.journey_missions.indexOf(pendingJourneyMission) + 1,
          missionTitle: pendingJourneyMission.mission.title,
        }
      }

      if (activeStageIndex < stages.length - 1) {
        const nextStage = stages[activeStageIndex + 1]
        if (nextStage?.journey_missions?.length) {
          return {
            journeyMissionId: nextStage.journey_missions[0].id,
            missionId: nextStage.journey_missions[0].mission.id,
            missionNumber: 1,
            missionTitle: nextStage.journey_missions[0].mission.title,
            isNextStage: true,
            nextStageTitle: nextStage.stage.title,
          }
        }
      }

      return null
    }

    const nextJourneyMission = getNextMission()

    return {
      journey: data,
      stages,
      currentStageIndex: activeStageIndex,
      currentStage,
      stagesProgress,
      questProgressPercentage,
      totalMissions,
      completedMissions,
      completedStageIds,
      nextJourneyMission,
    }
  }, [data, isStageCompleted])

  const calculateStageProgress = useCallback(
    (stageId: string): number => {
      if (!processedData?.stagesProgress) return 0
      const stageProgress = processedData.stagesProgress.find(
        (stage) => stage.id === stageId,
      )
      return stageProgress?.progressPercentage || 0
    },
    [processedData?.stagesProgress],
  )

  const journeyProgress = useMemo(() => {
    if (!processedData) return 0

    const journey = processedData.journey
    return (100 * journey.completed_points) / journey.total_points
  }, [processedData])

  return {
    ...processedData,
    loading: isLoading,
    error,
    isStageCompleted,
    calculateStageProgress,
    journeyProgress,
  }
}
